import React, { useState, useEffect } from 'react';
import * as LucideIcons from 'lucide-react';
import { 
  Plus, Edit2, Trash2, Save, X, Loader2, 
  ChevronLeft, ChevronRight, Star 
} from 'lucide-react';

// Lista de ícones sugeridos para o seletor visual
const ICONES_SUGERIDOS = [
  'ShieldCheck', 'Lock', 'Settings', 'HardDrive', 'Printer', 'Wifi', 
  'Video', 'Mail', 'Monitor', 'Key', 'Globe', 'Database', 'Cpu', 'Server'
];

const AdminProcedimentos = ({ showToast }) => {
  const [procedimentos, setProcedimentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editando, setEditando] = useState(null);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const itensPorPagina = 5;

  useEffect(() => {
    carregarProcedimentos();
  }, []);

  const carregarProcedimentos = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/procedimentos');
      const data = await res.json();
      setProcedimentos(data);
    } catch (err) {
      showToast("Erro ao carregar dados", "error");
    } finally {
      setLoading(false);
    }
  };

  const deletarProcedimento = async (id) => {
    if (!window.confirm("Deseja realmente excluir este card?")) return;
    try {
      const res = await fetch(`/api/procedimentos/${id}`, { method: 'DELETE' });
      if (res.ok) {
        showToast("Excluído com sucesso");
        carregarProcedimentos();
      }
    } catch (err) {
      showToast("Erro ao excluir", "error");
    }
  };

  const totalPaginas = Math.ceil(procedimentos.length / itensPorPagina);
  const itensPaginados = procedimentos.slice((paginaAtual - 1) * itensPorPagina, paginaAtual * itensPorPagina);

  if (loading && procedimentos.length === 0) {
    return (
      <div className="flex justify-center p-20">
        <Loader2 className="animate-spin text-emerald-500" size={40} />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20 relative animate-in fade-in duration-500">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white dark:bg-slate-900 p-6 rounded-[32px] border border-slate-200 dark:border-slate-800 shadow-sm">
        <div>
          <h1 className="text-2xl font-black dark:text-white tracking-tighter flex items-center gap-2">
            <LucideIcons.Settings className="text-emerald-500" /> Gerenciador
          </h1>
          <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest">Painel Administrativo de Dados</p>
        </div>
        <button 
          onClick={() => setEditando({ titulo: '', icone: 'Lock', cor: 'blue', tipo: 'lista', dados_json: [], ordem: 0 })}
          className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all shadow-lg shadow-emerald-500/20 active:scale-95"
        >
          <Plus size={18} /> Novo Procedimento
        </button>
      </div>

      {/* LISTAGEM COM EFEITO GLOW */}
      <div className="grid grid-cols-1 gap-4">
        {itensPaginados.map((proc) => {
          const Icone = LucideIcons[proc.icone] || LucideIcons.HelpCircle;
          return (
            <div key={proc.id} className="group relative">
              <div className={`absolute -inset-0.5 bg-gradient-to-r from-${proc.cor}-500 to-emerald-500 rounded-[24px] blur opacity-0 group-hover:opacity-30 transition duration-500`}></div>
              <div className="relative flex items-center justify-between bg-white dark:bg-slate-900 p-5 rounded-[22px] border border-slate-200 dark:border-slate-800 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className={`p-3 bg-${proc.cor}-500/10 rounded-xl text-${proc.cor}-500`}>
                    <Icone size={24} />
                  </div>
                  <div>
                    <h3 className="font-black dark:text-white text-sm">{proc.titulo}</h3>
                    <div className="flex gap-2 mt-1">
                      <span className="text-[9px] font-black uppercase px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-500 rounded-md">Ordem: {proc.ordem}</span>
                      <span className="text-[9px] font-black uppercase px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-500 rounded-md uppercase">{proc.tipo}</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setEditando(proc)} className="p-3 hover:bg-blue-50 dark:hover:bg-blue-500/10 text-blue-500 rounded-xl transition-all"><Edit2 size={18}/></button>
                  <button onClick={() => deletarProcedimento(proc.id)} className="p-3 hover:bg-red-50 dark:hover:bg-red-500/10 text-red-500 rounded-xl transition-all"><Trash2 size={18}/></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* PAGINAÇÃO */}
      {totalPaginas > 1 && (
        <div className="flex justify-center items-center gap-4 mt-8">
          <button disabled={paginaAtual === 1} onClick={() => setPaginaAtual(p => p - 1)} className="p-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 disabled:opacity-30"><ChevronLeft /></button>
          <span className="font-black text-xs uppercase tracking-widest text-slate-500">Página {paginaAtual} de {totalPaginas}</span>
          <button disabled={paginaAtual === totalPaginas} onClick={() => setPaginaAtual(p => p + 1)} className="p-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 disabled:opacity-30"><ChevronRight /></button>
        </div>
      )}

      {/* MODAL DE EDIÇÃO FIXO */}
      {editando && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 md:p-10">
          <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm animate-in fade-in duration-300" onClick={() => setEditando(null)}></div>
          <div className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white dark:bg-slate-900 rounded-[40px] shadow-2xl border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 duration-300">
            <FormProcedimento 
              item={editando} 
              onClose={() => setEditando(null)} 
              onSave={() => { carregarProcedimentos(); setEditando(null); showToast("Salvo com sucesso!"); }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const FormProcedimento = ({ item, onClose, onSave }) => {
  const [form, setForm] = useState({
    ...item,
    dados_json: typeof item.dados_json === 'string' ? JSON.parse(item.dados_json) : (item.dados_json || [])
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const metodo = form.id ? 'PUT' : 'POST';
    const url = form.id ? `/api/procedimentos/${form.id}` : '/api/procedimentos';
    const res = await fetch(url, {
      method: metodo,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form)
    });
    if (res.ok) onSave();
  };

  const updateItemJson = (index, field, value) => {
    const novosItens = [...form.dados_json];
    novosItens[index][field] = value;
    setForm({ ...form, dados_json: novosItens });
  };

  return (
    <form onSubmit={handleSubmit} className="p-8 space-y-6">
      <div className="flex justify-between items-center border-b border-slate-100 dark:border-slate-800 pb-6">
        <div>
          <h3 className="text-xl font-black dark:text-white uppercase tracking-tighter">Configurar Procedimento</h3>
          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">ID: {form.id || 'Novo'}</p>
        </div>
        <button type="button" onClick={onClose} className="p-2 bg-slate-100 dark:bg-slate-800 rounded-full text-slate-400 hover:text-red-500 transition-all"><X size={24}/></button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-1">
          <label className="text-[9px] font-black uppercase text-slate-400 px-1">Título</label>
          <input className="w-full p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 dark:text-white outline-none focus:border-emerald-500 transition-all" value={form.titulo} onChange={e => setForm({...form, titulo: e.target.value})} required />
        </div>
        <div className="space-y-1">
          <label className="text-[9px] font-black uppercase text-slate-400 px-1">Cor</label>
          <select className="w-full p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 dark:text-white outline-none focus:border-emerald-500 transition-all" value={form.cor} onChange={e => setForm({...form, cor: e.target.value})}>
            {['blue', 'red', 'emerald', 'pink', 'orange', 'purple', 'amber'].map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-[9px] font-black uppercase text-slate-400 px-1">Ordem</label>
          <input type="number" className="w-full p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 dark:text-white outline-none focus:border-emerald-500 transition-all" value={form.ordem} onChange={e => setForm({...form, ordem: e.target.value})} />
        </div>
      </div>

      {/* SELETOR DE ÍCONES RESPONSIVO (CORRIGIDO) */}
      <div className="space-y-2">
        <label className="text-[9px] font-black uppercase text-slate-400 px-1">Selecione o Ícone</label>
        <div className="grid grid-cols-4 md:grid-cols-7 gap-2 p-4 bg-slate-50 dark:bg-slate-950 rounded-2xl border border-slate-100 dark:border-slate-800">
          {ICONES_SUGERIDOS.map((iconName) => {
            const IconOption = LucideIcons[iconName];
            return (
              <button
                key={iconName}
                type="button"
                onClick={() => setForm({ ...form, icone: iconName })}
                className={`p-3 rounded-xl flex items-center justify-center transition-all active:scale-90 ${form.icone === iconName ? 'bg-emerald-500 text-white shadow-lg' : 'bg-white dark:bg-slate-900 text-slate-400 hover:text-emerald-500 border border-slate-200 dark:border-slate-800'}`}
              >
                <IconOption size={20} />
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-1">
        <label className="text-[9px] font-black uppercase text-slate-400 px-1">Tipo de Conteúdo</label>
        <div className="flex gap-2">
          {['lista', 'link'].map(t => (
            <button key={t} type="button" onClick={() => setForm({...form, tipo: t})} className={`flex-1 p-3 rounded-xl font-black text-[10px] uppercase tracking-widest border transition-all ${form.tipo === t ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900' : 'text-slate-400 border-slate-200'}`}>
              {t === 'lista' ? 'Procedimentos / Senhas' : 'Link Externo'}
            </button>
          ))}
        </div>
      </div>

      {form.tipo === 'link' ? (
        <div className="p-6 bg-emerald-500/5 rounded-3xl border border-emerald-500/20 space-y-4">
          <input className="w-full p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900" placeholder="URL Completa (https://...)" value={form.url || ''} onChange={e => setForm({...form, url: e.target.value})} />
          <input className="w-full p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900" placeholder="Título Ação" value={form.descricao || ''} onChange={e => setForm({...form, descricao: e.target.value})} />
          <input className="w-full p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900" placeholder="Legenda" value={form.sub_titulo || ''} onChange={e => setForm({...form, sub_titulo: e.target.value})} />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex justify-between items-center px-2">
            <label className="text-[9px] font-black uppercase text-slate-400">Itens da Lista</label>
            <button type="button" onClick={() => setForm({...form, dados_json: [...form.dados_json, { label: '', valor: '', destaque: false }]})} className="text-[10px] font-black text-emerald-500 uppercase hover:underline">+ Adicionar Linha</button>
          </div>
          <div className="max-h-60 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
            {form.dados_json.map((item, idx) => (
              <div key={idx} className="flex gap-2 items-center bg-white dark:bg-slate-900 p-3 rounded-xl border border-slate-200 dark:border-slate-800 group transition-all">
                <input className="flex-1 p-2 text-xs font-bold bg-transparent outline-none focus:border-emerald-500 border-b border-transparent" placeholder="Rótulo" value={item.label} onChange={e => updateItemJson(idx, 'label', e.target.value)} />
                <input className="flex-1 p-2 text-xs font-mono text-emerald-600 bg-transparent outline-none focus:border-emerald-500 border-b border-transparent" placeholder="Valor" value={item.valor} onChange={e => updateItemJson(idx, 'valor', e.target.value)} />
                <button type="button" onClick={() => {
                  const novos = [...form.dados_json];
                  novos[idx].destaque = !novos[idx].destaque;
                  setForm({...form, dados_json: novos});
                }} className={`p-2 rounded-lg transition-colors ${item.destaque ? 'text-emerald-500 bg-emerald-500/10' : 'text-slate-300'}`}><Star size={14} /></button>
                <button type="button" onClick={() => setForm({...form, dados_json: form.dados_json.filter((_, i) => i !== idx)})} className="text-slate-300 hover:text-red-500 p-2"><Trash2 size={16}/></button>
              </div>
            ))}
          </div>
        </div>
      )}

      <button type="submit" className="w-full bg-emerald-600 text-white p-5 rounded-[24px] font-black uppercase tracking-widest hover:bg-emerald-700 shadow-2xl shadow-emerald-500/30 transition-all flex items-center justify-center gap-3 active:scale-95">
        <Save size={20}/> Gravar no Banco de Dados
      </button>
    </form>
  );
};

export default AdminProcedimentos;